import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import QuickView from "@/components/QuickView";

const colorMap = {
  Black: "#1C1917", Espresso: "#3B2F2A", Grey: "#9CA3AF", Ivory: "#F5EFE6", Navy: "#1E293B",
  Champagne: "#D4AF37", Nude: "#E8D8CE", Camel: "#C19A6B", Beige: "#D8C4A8", White: "#FFFFFF",
  "Rose Taupe": "#B08D80", Mauve: "#C4A0A8", Sage: "#B2AC88", Olive: "#808000", Caramel: "#C68E17",
  "Dusty Blue": "#8DA0B3", Gold: "#D4AF37", Blush: "#E8B4B8", Rose: "#D9A5B3", Wine: "#722F37",
  Maroon: "#800020", Mint: "#A8D5BA", Mocha: "#6F4E37", "Rose Gold": "#E0BFB8", Cream: "#F5F0E1",
};

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const [qvOpen, setQvOpen] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const wished = inWishlist(product.id);
  const images = product.images?.length ? product.images : [""];
  const showImg = images[imgIdx] || images[0];
  const goPrev = (e) => { e.preventDefault(); e.stopPropagation(); setImgIdx((imgIdx - 1 + images.length) % images.length); };
  const goNext = (e) => { e.preventDefault(); e.stopPropagation(); setImgIdx((imgIdx + 1) % images.length); };
  const price = product.discount_price || product.price;
  const hasDiscount = !!product.discount_price;
  const off = hasDiscount ? Math.round(((product.price - product.discount_price) / product.price) * 100) : 0;

  return (
    <>
      <div className="group fade-in-up" style={{ animationDelay: `${(index % 4) * 80}ms` }} data-testid={`product-card-${product.slug}`}>
        <div className="relative overflow-hidden rounded-2xl bg-beige hover-zoom aspect-[3/4]">
          <Link to={`/product/${product.slug}`}>
            <img src={showImg} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
          </Link>

          {/* Image slider arrows */}
          {images.length > 1 && (
            <>
              <button onClick={goPrev} aria-label="Previous photo" data-testid={`img-prev-${product.slug}`} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-ivory/85 backdrop-blur flex items-center justify-center shadow hover:bg-ivory transition-colors">
                <ChevronLeft className="w-4 h-4 text-espresso" />
              </button>
              <button onClick={goNext} aria-label="Next photo" data-testid={`img-next-${product.slug}`} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-ivory/85 backdrop-blur flex items-center justify-center shadow hover:bg-ivory transition-colors">
                <ChevronRight className="w-4 h-4 text-espresso" />
              </button>
              <div className="absolute bottom-16 inset-x-0 flex justify-center gap-1.5 z-10">
                {images.map((_, d) => (
                  <span key={`d-${d}`} className={`h-1.5 rounded-full transition-all ${d === imgIdx ? "w-4 bg-ivory" : "w-1.5 bg-ivory/50"}`} />
                ))}
              </div>
            </>
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.is_new && <span className="bg-espresso text-ivory text-[10px] tracking-wider uppercase px-3 py-1 rounded-full font-medium">New</span>}
            {hasDiscount && <span className="bg-champagne text-espresso text-[10px] tracking-wider uppercase px-3 py-1 rounded-full font-semibold">-{off}%</span>}
          </div>

          {/* Wishlist */}
          <button
            onClick={() => toggleWishlist(product)}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-ivory/90 backdrop-blur flex items-center justify-center hover:bg-ivory transition-colors"
            data-testid={`wishlist-btn-${product.slug}`}
            aria-label="Wishlist"
          >
            <Heart className={`w-4 h-4 ${wished ? "fill-[#a13b2f] text-[#a13b2f]" : "text-espresso"}`} />
          </button>

          {/* Hover actions */}
          <div className="absolute inset-x-3 bottom-3 flex gap-2 translate-y-4 opacity-0 pointer-events-none group-hover:translate-y-0 group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-500">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToCart(product); }}
              className="flex-1 bg-espresso text-ivory text-xs font-medium py-2.5 rounded-full hover:bg-espresso/90 transition-colors flex items-center justify-center gap-1.5"
              data-testid={`add-cart-btn-${product.slug}`}
            >
              <ShoppingBag className="w-3.5 h-3.5" /> Add
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQvOpen(true); }}
              className="w-10 h-10 bg-ivory/95 rounded-full flex items-center justify-center hover:bg-champagne transition-colors"
              data-testid={`quickview-btn-${product.slug}`}
              aria-label="Quick view"
            >
              <Eye className="w-4 h-4 text-espresso" />
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="mt-3.5 px-0.5">
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-serif text-lg text-espresso leading-tight hover:text-champagne transition-colors">{product.name}</h3>
          </Link>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-sans font-semibold text-espresso text-sm">{inr(price)}</span>
            {hasDiscount && <span className="text-espresso/40 line-through text-xs">{inr(product.price)}</span>}
          </div>
          {/* Colors */}
          {product.colors?.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2">
              {product.colors.slice(0, 5).map((c) => (
                <span key={c} title={c} className="w-3.5 h-3.5 rounded-full border border-espresso/15" style={{ background: colorMap[c] || "#ccc" }} />
              ))}
            </div>
          )}
        </div>
      </div>

      <QuickView product={product} open={qvOpen} onClose={() => setQvOpen(false)} />
    </>
  );
}
